import { Layout, Select } from 'antd';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

const SIDEBAR_WIDTH = 256;
const SIDEBAR_COLLAPSED_WIDTH = 64;

type SidebarProps = {
  $isCollapsed: boolean;
};

export const SidebarContainer = styled(Layout.Sider)<SidebarProps>`
  && {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    height: 100vh;
    background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%);
    border-right: 1px solid #e8e8e8;
    z-index: 100;
    transition: all 0.3s ease-in-out;
    overflow: hidden;

    .ant-layout-sider-children {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
  }
`;

export const SidebarHeader = styled.div<SidebarProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
  min-height: 64px;

  .collapsed-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    overflow: hidden;
    border-radius: 8px;

    .ant-image {
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 32px;
        height: 32px;
        object-fit: contain;
      }
    }
  }

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      justify-content: center;
      padding: 12px 8px;
      border-bottom: none;
    `}
`;

export const LogoWrapper = styled.div<SidebarProps>`
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      width: 0;
      opacity: 0;
    `}

  .ant-image {
    img {
      max-height: 40px;
      object-fit: contain;
    }
  }
`;

export const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: color 0.2s;
  color: rgba(0, 0, 0, 0.35);

  &:hover {
    color: rgba(0, 0, 0, 0.65);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const CompanySwitcherWrapper = styled.div<SidebarProps>`
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  transition: all 0.2s ease;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      padding: 12px 8px;
      opacity: 0;
      height: 0;
      overflow: hidden;
      border-bottom: none;
    `}
`;

export const CompanySelectContainer = styled.div`
  position: relative;

  .company-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(0, 0, 0, 0.45);
    z-index: 1;
    pointer-events: none;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export const CompanySelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    border-style: dashed !important;
    height: 40px !important;
    display: flex;
    align-items: center;
    padding-left: 32px !important;
  }

  .ant-select-selection-item {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }
`;

export const NavSection = styled.nav<SidebarProps>`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 8px;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      padding: 12px 4px;
    `}
`;

type NavItemProps = {
  $isCollapsed: boolean;
  $isActive: boolean;
};

export const NavItem = styled(Link)<NavItemProps>`
  display: flex;
  align-items: center;
  height: 44px;
  margin: 4px 0;
  padding: 0 12px;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.55);
  text-decoration: none;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.75);
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: color-mix(in srgb, var(--primary) 10%, transparent);
      color: var(--primary);

      &:hover {
        background: color-mix(in srgb, var(--primary) 15%, transparent);
        color: var(--primary);
      }
    `}

  ${({ $isCollapsed }) =>
    $isCollapsed
      ? css`
          justify-content: center;
          padding: 0;
          width: 44px;
          margin: 4px auto;
        `
      : css`
          span {
            margin-left: 12px;
            white-space: nowrap;
            overflow: hidden;
          }
        `}
`;

export const UserSection = styled.div<SidebarProps>`
  padding: 16px;
  border-top: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 12px;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      justify-content: center;
      padding: 12px 8px;
    `}
`;

export const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--primary-light, rgba(24, 144, 255, 0.1));
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

export const UserInfo = styled.div<SidebarProps>`
  flex: 1;
  min-width: 0;
  transition: opacity 0.2s, width 0.2s;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      opacity: 0;
      width: 0;
      overflow: hidden;
    `}
`;

export const UserName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CustomerName = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.45);

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: var(--danger, #ff4d4f);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const WhatsappSection = styled.div<SidebarProps>`
  padding: 8px;
  border-top: 1px solid #e8e8e8;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      padding: 8px 4px;
      display: flex;
      justify-content: center;
    `}
`;

export const WhatsappContainer = styled.div<SidebarProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 12px;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-radius: 8px;
  border: 1px solid #bbf7d0;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      padding: 0;
      background: transparent;
      border: none;
    `}
`;

export const WhatsappText = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #166534;
  margin-bottom: 8px;
  text-align: center;
  line-height: 1.3;
`;

export const WhatsappButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  background-color: #25d366;
  color: white !important;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.25);

  &:hover {
    background-color: #20c057;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.35);
    color: white !important;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const WhatsappIconButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #25d366;
  color: white !important;
  border-radius: 10px;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.25);

  &:hover {
    background-color: #20c057;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.35);
    color: white !important;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
